import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useMemo, type SyntheticEvent } from 'react';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Link as RouterLink } from 'react-router-dom';

import VNChip from '@/components/VNChip/VNChip';
import {
  getNotificationAgeText,
  getNotificationKindIcon,
  getNotificationKindLabel,
  getNotificationSeverityIcon,
  getNotificationSeverityTone,
  NOTIFICATION_CATEGORY_OPTIONS,
  NotificationKind,
  NotificationSeverity,
  type NotificationCategory,
} from '@/models/VnMessageService';
import type { NotificationMessageDto } from '@/models/VnMessageService';
import { dateUtils } from '@/utils/dateUtils';

import NotificationBodyContent from './NotificationBodyContent';

interface NotificationDisplayProps {
  notification: NotificationMessageDto;
  isPreviewMode?: boolean;
  onClosePreview?: () => void;
  /** When true, omit title/summary/timestamp chrome (hub detail pane already shows them). */
  bodyOnly?: boolean;
  className?: string;
}

const CATEGORY_LABEL_BY_VALUE = NOTIFICATION_CATEGORY_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {} as Record<NotificationCategory, string>
);

/** Maps an alert severity to a v2 design-system color token. */
const SEVERITY_TOKEN: Record<string, string> = {
  critical: 'destructive',
  warning: 'warning',
  info: 'primary',
  resolved: 'success',
};

/**
 * Presentational renderer for a full notification takeover, including optional
 * preview banner, close control, header, body content, and CTA.
 */
const NotificationDisplay = ({
  notification,
  isPreviewMode = false,
  onClosePreview,
  bodyOnly = false,
  className = '',
}: NotificationDisplayProps) => {
  const bodyFormat = notification.kind === NotificationKind.Message ? 'html' : 'richtext';
  const rootClassName = `notification-body v2 ${bodyOnly ? 'notification-body--body-only' : ''} ${className}`.trim();
  const isAlert = notification.kind === NotificationKind.Alert;
  const KindIcon = getNotificationKindIcon(notification.kind, notification.severity);
  const SeverityIcon = getNotificationSeverityIcon(notification.severity);
  const categoryLabel = CATEGORY_LABEL_BY_VALUE[notification.category] ?? notification.category;
  const subjectLabel = getNotificationKindLabel(notification.kind, notification.severity);
  const relativeTime = dateUtils.formatRelativeTime(notification.sendDate ?? notification.createdDate);
  const summary = String(notification.summary ?? '').trim();
  const hasSummary = Boolean(summary);
  const severityLabel = notification.severity ?? NotificationSeverity.Warning;
  const severityToken = SEVERITY_TOKEN[String(severityLabel).toLowerCase()] ?? 'border';
  const summaryToneClassName = `border-${severityToken} bg-${severityToken}/10 text-${severityToken}`;
  const kindIconClassName = isAlert
    ? `notification-center__kind-icon vn-chip--${getNotificationSeverityTone(notification.severity)}`
    : 'notification-center__kind-icon notification-center__kind-icon--announcement';
  const hasCta = Boolean(notification.cta?.label && notification.cta?.url);
  const ctaUrl = notification.cta?.url ?? '';
  const ctaLabel = notification.cta?.label ?? '';

  const bodyContent = (
    <NotificationBodyContent body={notification.body} format={bodyFormat} bodyOnly={bodyOnly} />
  );

  const ctaContent = hasCta ? (
    <div className="notification-body__cta">
      {ctaUrl.startsWith('/') ? (
        <Button variant="contained" color="primary" component={RouterLink} to={ctaUrl} endIcon={<OpenInNewIcon />}>
          {ctaLabel}
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          component="a"
          href={ctaUrl}
          target="_blank"
          rel="noreferrer"
          endIcon={<OpenInNewIcon />}
        >
          {ctaLabel}
        </Button>
      )}
    </div>
  ) : null;

  if (bodyOnly) {
    return (
      <div className={rootClassName}>
        <div className="notification-center__body">{bodyContent}</div>
        {ctaContent}
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      {isPreviewMode && (
        <div className="notification-center__preview-banner">
          <VNChip label="PREVIEW" className="notification-center__preview-chip" />
          <span>This is a preview of how the message will appear as a full-screen takeover.</span>
        </div>
      )}
      <div className="notification-center__header t-caption">
        <div className="notification-center__header-subject">
          <KindIcon fontSize="small" className={kindIconClassName} />
          <span>
            {subjectLabel}
            {categoryLabel ? (
              <>
                <span aria-hidden="true"> &middot; </span>
                {categoryLabel}
              </>
            ) : null}
          </span>
        </div>
        {isPreviewMode && (
          <IconButton
            className="notification-center__preview-close"
            size="small"
            aria-label="Close preview"
            onClick={onClosePreview}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <div className="notification-center__content">
        <div className="notification-center__title">
          <h3 className="t-section-title">{notification.title}</h3>
          {relativeTime ? (
            <div className="notification-center__title-subscript">
              <AccessTimeOutlinedIcon fontSize="inherit" />
              <span className="t-caption">{relativeTime}</span>
            </div>
          ) : null}
        </div>
        {hasSummary &&
          (isAlert ? (
            <div className={`notification-center__summary ${summaryToneClassName} t-body`}>
              <div className="notification-center__summary-severity">
                <SeverityIcon fontSize="inherit" />
                <span>{severityLabel}</span>
              </div>
              <span aria-hidden="true">&middot;</span>
              <span className="notification-center__summary-text">{summary}</span>
            </div>
          ) : (
            <p className="notification-center__summary notification-center__summary--message t-body">{summary}</p>
          ))}
        <div className="notification-center__body">{bodyContent}</div>
      </div>
      {ctaContent}
    </div>
  );
};

export default NotificationDisplay;
